(() => {
  'use strict';

  if (window.CMProductPageRuntime) {
    window.CMProductPageRuntime.initialise(document);
    return;
  }

  const PRODUCT_SELECTOR = '[data-cm-product]';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ProductGallery {
    constructor(root, signal) {
      this.root = root;
      this.signal = signal;
      this.track = root?.querySelector('[data-cm-gallery-track]') || null;
      this.slides = this.track ? [...this.track.querySelectorAll('[data-cm-gallery-slide]')] : [];
      this.thumbnails = root ? [...root.querySelectorAll('[data-cm-gallery-thumbnail]')] : [];
      this.previousButton = root?.querySelector('[data-cm-gallery-previous]') || null;
      this.nextButton = root?.querySelector('[data-cm-gallery-next]') || null;
      this.position = root?.querySelector('[data-cm-gallery-position]') || null;
      this.isLooping = this.slides.length > 1;
      this.clones = [];
      this.railSlides = [];
      this.scrollFrame = 0;
      this.scrollEndFrame = 0;
      this.resizeFrame = 0;
      this.repositionFrame = 0;
      this.lastScrollLeft = 0;
      this.stableScrollFrames = 0;
      this.isPointerDown = false;
      this.isRepositioning = false;
      this.isNavigating = false;
      this.navigationQueue = [];
      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));

      if (!this.root || !this.track || this.slides.length === 0) return;

      this.slides.forEach((slide, index) => {
        slide.dataset.cmRealIndex = String(index);
        slide.setAttribute('data-cm-gallery-presentation', '');
      });

      if (this.isLooping) this.createLoopClones();
      this.railSlides = [...this.track.querySelectorAll('[data-cm-gallery-presentation]')];
      this.presentationIndex = this.isLooping ? this.slides.length + this.activeIndex : this.activeIndex;

      this.previousButton?.addEventListener('click', () => this.queueStep(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.queueStep(1), { signal });

      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener(
          'click',
          () => this.activateMedia(thumbnail.dataset.cmMediaId),
          { signal },
        );
      });

      this.track.addEventListener('scroll', () => this.handleScroll(), { passive: true, signal });
      this.track.addEventListener('scrollend', () => this.handleScrollEnd(), { passive: true, signal });
      this.track.addEventListener('pointerdown', () => {
        this.isPointerDown = true;
        this.navigationQueue.length = 0;
        this.isNavigating = false;
      }, { passive: true, signal });
      this.track.addEventListener('pointerup', () => {
        this.isPointerDown = false;
        this.watchForScrollEnd();
      }, { passive: true, signal });
      this.track.addEventListener('pointercancel', () => {
        this.isPointerDown = false;
        this.watchForScrollEnd();
      }, { passive: true, signal });
      this.track.addEventListener(
        'keydown',
        (event) => {
          if (event.target !== this.track) return;
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.queueStep(-1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.queueStep(1);
          }
        },
        { signal },
      );

      window.addEventListener('resize', () => this.handleResize(), { passive: true, signal });

      this.updateActiveState();
      if (this.isLooping) this.track.classList.add('is-preparing');
      requestAnimationFrame(() => {
        this.jumpToPresentation(this.presentationIndex);
        requestAnimationFrame(() => this.track.classList.remove('is-preparing'));
      });
    }

    createLoopClones() {
      const prefix = document.createDocumentFragment();
      const suffix = document.createDocumentFragment();

      this.slides.forEach((slide, index) => {
        prefix.append(this.createPresentationClone(slide, index));
        suffix.append(this.createPresentationClone(slide, index));
      });

      this.track.insertBefore(prefix, this.slides[0]);
      this.track.append(suffix);
      this.clones = [...this.track.querySelectorAll('[data-cm-gallery-clone]')];
    }

    createPresentationClone(slide, realIndex) {
      const previewSource = slide.dataset.cmMediaPreview;
      const clone = slide.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('data-cm-gallery-slide');
      clone.removeAttribute('data-cm-media-id');
      clone.removeAttribute('data-cm-media-position');
      clone.removeAttribute('data-cm-media-preview');
      clone.removeAttribute('role');
      clone.removeAttribute('aria-roledescription');
      clone.removeAttribute('aria-label');
      clone.classList.remove('is-active', 'is-current');
      clone.classList.add('is-clone');
      clone.dataset.cmRealIndex = String(realIndex);
      clone.setAttribute('data-cm-gallery-clone', '');
      clone.setAttribute('data-cm-gallery-presentation', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');

      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea, video, audio, model-viewer, iframe, [tabindex]')
        .forEach((element) => element.setAttribute('tabindex', '-1'));

      const frame = clone.querySelector('.cm-product-gallery__media-frame');
      if (frame && previewSource) {
        const image = document.createElement('img');
        image.className = 'cm-product-gallery__image cm-product-gallery__clone-image';
        image.src = previewSource;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        frame.replaceChildren(image);
      }

      return clone;
    }

    activate(index, shouldScroll = true) {
      if (!Number.isInteger(index) || this.slides.length === 0) return;
      this.syncStateToScroll();
      this.normaliseLoopPosition();
      const nextIndex = this.isLooping
        ? (index % this.slides.length + this.slides.length) % this.slides.length
        : Math.min(Math.max(index, 0), this.slides.length - 1);

      this.activeIndex = nextIndex;
      this.navigationQueue.length = 0;
      this.presentationIndex = this.isLooping ? this.slides.length + nextIndex : nextIndex;
      this.isNavigating = shouldScroll;
      this.updateActiveState();
      if (shouldScroll) this.navigateToPresentation(this.presentationIndex, true);
    }

    activateMedia(mediaId) {
      if (!mediaId) return;
      const index = this.slides.findIndex((slide) => String(slide.dataset.cmMediaId) === String(mediaId));
      if (index >= 0) this.activate(index);
    }

    queueStep(direction) {
      if (!this.isLooping) return;
      this.navigationQueue.push(direction < 0 ? -1 : 1);
      this.processNavigationQueue();
    }

    processNavigationQueue() {
      if (this.isNavigating || this.navigationQueue.length === 0) return;
      this.normaliseLoopPosition();

      const direction = this.navigationQueue.shift();
      const targetIndex = this.presentationIndex + direction;
      const targetSlide = this.railSlides[targetIndex];
      if (!targetSlide) return;

      this.isNavigating = true;
      this.presentationIndex = targetIndex;
      this.activeIndex = Number(targetSlide.dataset.cmRealIndex);
      this.updateActiveState();
      this.navigateToPresentation(targetIndex, true);
    }

    targetScrollLeft(slide) {
      if (!slide || !this.track) return 0;
      const centred = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
      return Math.min(Math.max(0, centred), Math.max(0, this.track.scrollWidth - this.track.clientWidth));
    }

    navigateToPresentation(index, smooth = true) {
      const slide = this.railSlides[index];
      if (!slide || !this.track) return;

      const useSmoothScroll = smooth && !reduceMotion.matches;
      this.track.scrollTo({
        left: this.targetScrollLeft(slide),
        behavior: useSmoothScroll ? 'smooth' : 'auto',
      });
      if (!useSmoothScroll) requestAnimationFrame(() => this.handleScrollEnd());
    }

    jumpToPresentation(index) {
      const slide = this.railSlides[index];
      if (!slide || !this.track) return;

      this.isRepositioning = true;
      this.presentationIndex = index;
      this.track.scrollTo({ left: this.targetScrollLeft(slide), behavior: 'auto' });
      if (this.repositionFrame) cancelAnimationFrame(this.repositionFrame);
      this.repositionFrame = requestAnimationFrame(() => {
        this.repositionFrame = 0;
        this.isRepositioning = false;
      });
    }

    handleScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        if (this.isRepositioning) return;
        this.syncStateToScroll();
      });
      this.watchForScrollEnd();
    }

    syncStateToScroll() {
      if (this.railSlides.length === 0) return;
      const trackCentre = this.track.scrollLeft + this.track.clientWidth / 2;
      const closestIndex = this.railSlides.reduce((closest, slide, index) => {
        const centre = slide.offsetLeft + slide.offsetWidth / 2;
        const closestCentre = this.railSlides[closest].offsetLeft + this.railSlides[closest].offsetWidth / 2;
        return Math.abs(centre - trackCentre) < Math.abs(closestCentre - trackCentre) ? index : closest;
      }, 0);

      const closestSlide = this.railSlides[closestIndex];
      const nextActiveIndex = Number(closestSlide.dataset.cmRealIndex);
      if (closestIndex === this.presentationIndex && nextActiveIndex === this.activeIndex) return;

      this.presentationIndex = closestIndex;
      this.activeIndex = nextActiveIndex;
      this.updateActiveState();
    }

    watchForScrollEnd() {
      if (this.scrollEndFrame) return;
      this.lastScrollLeft = this.track.scrollLeft;
      this.stableScrollFrames = 0;

      const check = () => {
        if (this.isPointerDown) {
          this.lastScrollLeft = this.track.scrollLeft;
          this.stableScrollFrames = 0;
          this.scrollEndFrame = requestAnimationFrame(check);
          return;
        }

        const currentScrollLeft = this.track.scrollLeft;
        if (Math.abs(currentScrollLeft - this.lastScrollLeft) <= 0.5) this.stableScrollFrames += 1;
        else this.stableScrollFrames = 0;
        this.lastScrollLeft = currentScrollLeft;

        if (this.stableScrollFrames >= 3) {
          this.scrollEndFrame = 0;
          this.handleScrollEnd();
          return;
        }
        this.scrollEndFrame = requestAnimationFrame(check);
      };

      this.scrollEndFrame = requestAnimationFrame(check);
    }

    handleScrollEnd() {
      if (this.isRepositioning) return;
      if (this.scrollEndFrame) cancelAnimationFrame(this.scrollEndFrame);
      this.scrollEndFrame = 0;
      this.syncStateToScroll();
      this.normaliseLoopPosition();
      this.isNavigating = false;
      this.processNavigationQueue();
    }

    normaliseLoopPosition() {
      if (!this.isLooping) return;
      const realCount = this.slides.length;
      if (this.presentationIndex >= realCount && this.presentationIndex < realCount * 2) return;

      const realIndex = Number(this.railSlides[this.presentationIndex]?.dataset.cmRealIndex);
      if (!Number.isInteger(realIndex)) return;
      this.activeIndex = realIndex;
      this.jumpToPresentation(realCount + realIndex);
      this.updateActiveState();
    }

    handleResize() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = 0;
        const targetIndex = this.isLooping ? this.slides.length + this.activeIndex : this.activeIndex;
        this.jumpToPresentation(targetIndex);
        this.updateActiveState();
      });
    }

    updateActiveState() {
      this.slides.forEach((slide, index) => {
        const active = index === this.activeIndex;
        slide.classList.toggle('is-active', active);
        if (!active) slide.querySelector('video')?.pause();
      });

      this.railSlides.forEach((slide, index) => {
        slide.classList.toggle('is-current', index === this.presentationIndex);
      });

      this.thumbnails.forEach((thumbnail) => {
        const active =
          String(thumbnail.dataset.cmMediaId) === String(this.slides[this.activeIndex]?.dataset.cmMediaId);
        thumbnail.classList.toggle('is-active', active);
        if (active) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });

      if (this.position) this.position.textContent = String(this.activeIndex + 1);
      [this.previousButton, this.nextButton].forEach((button) => {
        if (!button) return;
        button.hidden = !this.isLooping;
        button.disabled = !this.isLooping;
        if (this.isLooping) button.removeAttribute('aria-hidden');
        else button.setAttribute('aria-hidden', 'true');
      });
    }

    destroy() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.scrollEndFrame) cancelAnimationFrame(this.scrollEndFrame);
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      if (this.repositionFrame) cancelAnimationFrame(this.repositionFrame);
      this.clones.forEach((clone) => clone.remove());
      this.slides.forEach((slide) => {
        slide.removeAttribute('data-cm-real-index');
        slide.removeAttribute('data-cm-gallery-presentation');
        slide.classList.remove('is-current');
      });
      this.track?.classList.remove('is-preparing');
    }
  }

  class ProductPage {
    constructor(root) {
      this.root = root;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.form = root.querySelector('.cm-product-purchase__form');
      this.variantSelect = root.querySelector('[data-cm-variant-select]');
      this.nativeVariant = root.querySelector('[data-cm-native-variant]');
      this.optionControls = [...root.querySelectorAll('[data-cm-option-control]')];
      this.enhancedControls = [...root.querySelectorAll('[data-cm-enhanced-control]')];
      this.price = root.querySelector('[data-cm-price]');
      this.comparePrice = root.querySelector('[data-cm-compare-price]');
      this.unitPrice = root.querySelector('[data-cm-unit-price]');
      this.availability = root.querySelector('[data-cm-availability]');
      this.availabilityText = root.querySelector('[data-cm-availability-text]');
      this.purchase = root.querySelector('.cm-product-purchase');
      this.addButton = root.querySelector('[data-cm-add-to-bag]');
      this.addButtonText = root.querySelector('[data-cm-add-to-bag-text]');
      this.liveStatus = root.querySelector('[data-cm-live-status]');
      this.quantityBlock = root.querySelector('[data-cm-quantity-block]');
      this.quantityInput = root.querySelector('[data-cm-quantity-input]');
      this.quantityMinus = root.querySelector('[data-cm-quantity-minus]');
      this.quantityPlus = root.querySelector('[data-cm-quantity-plus]');
      this.conditionValues = [...root.querySelectorAll('[data-cm-condition-value]')];
      this.skuValues = [...root.querySelectorAll('[data-cm-sku-value]')];
      this.weightRow = root.querySelector('[data-cm-weight-row]');
      this.weightValue = root.querySelector('[data-cm-weight-value]');
      this.details = root.querySelector('[data-cm-product-details]');
      this.gallery = new ProductGallery(root.querySelector('[data-cm-product-gallery]'), this.signal);
      this.addPending = false;
      this.productTitle = this.purchase?.dataset.cmProductTitle || '';
      this.messages = {
        addToBag: this.purchase?.dataset.cmLabelAddToBag || '',
        adding: this.purchase?.dataset.cmLabelAdding || '',
        soldOut: this.purchase?.dataset.cmLabelSoldOut || '',
        added: this.purchase?.dataset.cmMessageAdded || '',
        addError: this.purchase?.dataset.cmMessageAddError || '',
        addUnknown: this.purchase?.dataset.cmMessageAddUnknown || '',
      };

      const variantData = root.querySelector('[data-cm-product-variants]');
      try {
        this.variants = JSON.parse(variantData?.textContent || '[]');
      } catch (error) {
        this.variants = [];
        console.error('Collect Monsters product variant data could not be parsed.', error);
      }

      this.currentVariant = this.findVariantById(this.variantSelect?.value) || this.variants[0] || null;
      this.selectedOptions = this.currentVariant?.options ? [...this.currentVariant.options] : [];
      this.conditionPosition = Number(
        root.querySelector('.cm-product-purchase__condition [data-cm-option-control]')?.dataset.optionPosition || 0,
      );

      this.bindEvents();
      this.enableEnhancedControls();
      this.renderVariant(this.currentVariant, { announce: false, updateUrl: false, activateMedia: false });
      this.root.dataset.cmProductInitialized = 'true';
    }

    bindEvents() {
      this.optionControls.forEach((control) => {
        control.addEventListener('change', (event) => this.handleOptionChange(event.currentTarget), {
          signal: this.signal,
        });
      });

      if (this.variantSelect?.tagName === 'SELECT') {
        this.variantSelect.addEventListener(
          'change',
          () => {
            if (this.optionControls.length > 0) return;
            const variant = this.findVariantById(this.variantSelect.value);
            if (variant) this.selectedOptions = [...variant.options];
            this.renderVariant(variant, { announce: true, updateUrl: true, activateMedia: true });
          },
          { signal: this.signal },
        );
      }

      this.quantityMinus?.addEventListener('click', () => this.stepQuantity(-1), { signal: this.signal });
      this.quantityPlus?.addEventListener('click', () => this.stepQuantity(1), { signal: this.signal });
      this.quantityInput?.addEventListener('input', () => this.updateQuantityButtons(), { signal: this.signal });
      this.quantityInput?.addEventListener('change', () => this.normalizeQuantity(), { signal: this.signal });

      this.form?.addEventListener(
        'submit',
        async (event) => {
          if (event.submitter && event.submitter !== this.addButton) return;
          if (!this.currentVariant || !this.currentVariant.available) {
            event.preventDefault();
            this.announce(this.currentVariant ? 'This variant is sold out.' : 'This option combination is unavailable.');
            return;
          }
          event.preventDefault();
          await this.addToBag();
        },
        { signal: this.signal },
      );
    }

    async addToBag() {
      if (!this.form || !this.addButton || this.addPending || this.addButton.disabled) return;

      const error = this.root.querySelector('[data-cm-add-error]');
      const submittedTitle = this.productTitle;
      const formData = new FormData(this.form);
      const header = document.querySelector('collect-monsters-header[data-section-id]');
      const headerSectionId = header?.dataset.sectionId || '';
      // Ask Shopify to render the configured Header from the same post-add cart
      // state, so the Bag preview never depends on a second request succeeding.
      if (headerSectionId) {
        formData.set('sections', headerSectionId);
        formData.set('sections_url', window.Shopify?.routes?.root || '/');
      }
      this.addPending = true;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      if (this.addButtonText) this.addButtonText.textContent = this.messages.adding;
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }

      try {
        let response;
        try {
          response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          });
        } catch {
          document.dispatchEvent(new CustomEvent('cm:cart-updated', { detail: { source: 'product-add-unknown' } }));
          this.showAddError(error, this.messages.addUnknown);
          return;
        }

        const result = await this.readJsonResponse(response);

        if (!response.ok) {
          document.dispatchEvent(
            new CustomEvent('cm:cart-updated', {
              detail: {
                cart: result && Number.isInteger(Number(result.item_count)) ? result : null,
                sections: result?.sections || null,
                sectionId: headerSectionId || null,
                source: 'product-add-error',
              },
            }),
          );
          this.showAddError(error, result?.description || result?.message || this.messages.addError);
          return;
        }

        document.dispatchEvent(
          new CustomEvent('cm:cart-updated', {
            detail: {
              cart: result && Number.isInteger(Number(result.item_count)) ? result : null,
              sections: result?.sections || null,
              sectionId: headerSectionId || null,
              openBag: true,
              source: 'product-add',
            },
          }),
        );
        this.announce(this.messages.added.replace('__TITLE__', submittedTitle));
      } catch {
        this.showAddError(error, this.messages.addError);
      } finally {
        this.addPending = false;
        this.addButton.removeAttribute('aria-busy');
        this.renderAddButton(
          this.currentVariant?.available ? this.messages.addToBag : this.messages.soldOut,
          !this.currentVariant?.available,
        );
      }
    }

    async readJsonResponse(response) {
      try {
        return await response.json();
      } catch {
        return null;
      }
    }

    showAddError(error, message) {
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    enableEnhancedControls() {
      this.optionControls.forEach((control) => {
        control.disabled = false;
      });
      this.enhancedControls.forEach((element) => {
        element.hidden = false;
      });
      if (this.nativeVariant && this.optionControls.length > 0) this.nativeVariant.hidden = true;
      this.root.classList.add('is-enhanced');
    }

    handleOptionChange(control) {
      if (control.matches('input[type="radio"]') && !control.checked) return;
      const position = Number(control.dataset.optionPosition);
      if (!position) return;

      this.selectedOptions[position - 1] = control.value;
      const variant = this.variants.find(
        (candidate) =>
          candidate.options.length === this.selectedOptions.length &&
          candidate.options.every((value, index) => value === this.selectedOptions[index]),
      );
      this.renderVariant(variant || null, { announce: true, updateUrl: true, activateMedia: true });
    }

    renderVariant(variant, { announce, updateUrl, activateMedia }) {
      this.currentVariant = variant;

      if (!variant) {
        if (this.variantSelect) this.variantSelect.value = '';
        if (this.price) this.price.textContent = '—';
        if (this.comparePrice) this.comparePrice.hidden = true;
        if (this.unitPrice) this.unitPrice.hidden = true;
        this.renderAvailability('Unavailable', 'unavailable');
        this.renderAddButton('Unavailable', true);
        this.updateQuantityRule(null);
        this.updateCondition(this.selectedOptions[this.conditionPosition - 1] || '');
        this.updateSku('');
        this.updateWeight(null);
        if (updateUrl && !window.Shopify?.designMode) this.clearVariantUrl();
        if (announce) this.announce('This option combination is unavailable.');
        return;
      }

      if (this.variantSelect) this.variantSelect.value = String(variant.id);
      if (this.price) this.price.textContent = variant.priceFormatted;

      if (this.comparePrice) {
        const showCompare = Number.isFinite(variant.compareAtPrice) && variant.compareAtPrice > variant.price;
        this.comparePrice.hidden = !showCompare;
        this.comparePrice.textContent = showCompare ? variant.compareAtPriceFormatted : '';
      }

      if (this.unitPrice) {
        const showUnitPrice = Number.isFinite(variant.unitPrice) && Boolean(variant.unitPriceReference);
        this.unitPrice.hidden = !showUnitPrice;
        this.unitPrice.textContent = showUnitPrice
          ? `${variant.unitPriceFormatted} / ${variant.unitPriceReference}`
          : '';
      }

      const availabilityLabel = variant.available ? 'Available' : this.messages.soldOut;
      const availabilityState = variant.available ? 'available' : 'sold-out';
      this.renderAvailability(availabilityLabel, availabilityState);
      this.renderAddButton(variant.available ? this.messages.addToBag : this.messages.soldOut, !variant.available);
      this.updateQuantityRule(variant);
      this.updateCondition(variant.options[this.conditionPosition - 1] || '');
      this.updateSku(variant.sku || '');
      this.updateWeight(variant);

      if (activateMedia && variant.featuredMediaId) this.gallery.activateMedia(variant.featuredMediaId);
      if (updateUrl && !window.Shopify?.designMode) this.updateVariantUrl(variant.id);
      if (announce) this.announce(`${variant.priceFormatted}. ${availabilityLabel}.`);
    }

    renderAvailability(label, state) {
      if (this.availabilityText) this.availabilityText.textContent = label;
      if (!this.availability) return;
      this.availability.dataset.availabilityState = state;
      this.availability.classList.remove('is-available', 'is-sold-out', 'is-unavailable');
      this.availability.classList.add(`is-${state}`);
    }

    renderAddButton(label, disabled) {
      if (this.addButtonText) this.addButtonText.textContent = label;
      if (this.addButton) this.addButton.disabled = disabled || this.addPending;
    }

    updateCondition(value) {
      if (!value) return;
      this.conditionValues.forEach((element) => {
        element.textContent = value;
      });
    }

    updateSku(value) {
      this.skuValues.forEach((element) => {
        element.textContent = value || '—';
      });
    }

    updateWeight(variant) {
      if (!this.weightRow || !this.weightValue) return;
      const hasWeight = Number.isFinite(variant?.weight) && variant.weight > 0 && Boolean(variant.weightFormatted);
      this.weightValue.textContent = hasWeight ? variant.weightFormatted : '';
      this.weightRow.hidden = !hasWeight;
      if (this.details) {
        this.details.hidden = !this.details.querySelector('.cm-product-details__row:not([hidden])');
      }
    }

    updateQuantityRule(variant) {
      const available = Boolean(variant?.available);
      if (this.quantityBlock) this.quantityBlock.hidden = !available;
      if (!this.quantityInput) return;

      const minimum = Number(variant?.quantityRule?.min) || 1;
      const increment = Number(variant?.quantityRule?.increment) || 1;
      const maximum = Number(variant?.quantityRule?.max);

      this.quantityInput.min = String(minimum);
      this.quantityInput.step = String(increment);
      if (Number.isFinite(maximum) && maximum > 0) this.quantityInput.max = String(maximum);
      else this.quantityInput.removeAttribute('max');
      this.quantityInput.disabled = !available;
      if (this.quantityMinus) this.quantityMinus.disabled = !available;
      if (this.quantityPlus) this.quantityPlus.disabled = !available;

      const current = Number(this.quantityInput.value);
      if (!Number.isFinite(current) || current < minimum) this.quantityInput.value = String(minimum);
      this.normalizeQuantity();
    }

    stepQuantity(direction) {
      if (!this.quantityInput || this.quantityInput.disabled) return;
      const minimum = Number(this.quantityInput.min) || 1;
      const increment = Number(this.quantityInput.step) || 1;
      const maximum = Number(this.quantityInput.max);
      const current = Number(this.quantityInput.value) || minimum;
      let next = current + direction * increment;

      next = Math.max(minimum, next);
      if (Number.isFinite(maximum) && maximum > 0) next = Math.min(maximum, next);
      this.quantityInput.value = String(next);
      this.updateQuantityButtons();
    }

    normalizeQuantity() {
      if (!this.quantityInput || this.quantityInput.disabled) {
        this.updateQuantityButtons();
        return;
      }

      const minimum = Number(this.quantityInput.min) || 1;
      const increment = Number(this.quantityInput.step) || 1;
      const maximum = Number(this.quantityInput.max);
      let value = Number(this.quantityInput.value);
      if (!Number.isFinite(value)) value = minimum;

      value = minimum + Math.round((value - minimum) / increment) * increment;
      value = Math.max(minimum, value);
      if (Number.isFinite(maximum) && maximum > 0) value = Math.min(maximum, value);
      this.quantityInput.value = String(value);
      this.updateQuantityButtons();
    }

    updateQuantityButtons() {
      if (!this.quantityInput) return;
      const value = Number(this.quantityInput.value);
      const minimum = Number(this.quantityInput.min) || 1;
      const maximum = Number(this.quantityInput.max);
      const unavailable = this.quantityInput.disabled;

      if (this.quantityMinus) this.quantityMinus.disabled = unavailable || !Number.isFinite(value) || value <= minimum;
      if (this.quantityPlus) {
        this.quantityPlus.disabled =
          unavailable || (Number.isFinite(maximum) && maximum > 0 && Number.isFinite(value) && value >= maximum);
      }
    }

    updateVariantUrl(variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState({ ...window.history.state, variantId }, '', url);
    }

    clearVariantUrl() {
      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      window.history.replaceState({ ...window.history.state, variantId: null }, '', url);
    }

    findVariantById(id) {
      return this.variants.find((variant) => String(variant.id) === String(id)) || null;
    }

    announce(message) {
      if (!this.liveStatus) return;
      this.liveStatus.textContent = '';
      requestAnimationFrame(() => {
        this.liveStatus.textContent = message;
      });
    }

    destroy() {
      this.gallery.destroy();
      this.abortController.abort();
      this.root.classList.remove('is-enhanced');
      delete this.root.dataset.cmProductInitialized;
      delete this.root.cmProductPage;
    }
  }

  const initialise = (scope = document) => {
    const roots = [];
    if (scope.matches?.(PRODUCT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll?.(PRODUCT_SELECTOR) || []);

    roots.forEach((root) => {
      if (root.cmProductPage) return;
      root.cmProductPage = new ProductPage(root);
    });
  };

  const unload = (scope) => {
    const roots = [];
    if (scope.matches?.(PRODUCT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll?.(PRODUCT_SELECTOR) || []);
    roots.forEach((root) => root.cmProductPage?.destroy());
  };

  window.CMProductPageRuntime = { initialise, unload };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();

